import useAxios from "./useAxios";
import usePfqAxios from "./usePfqAxios";
import Constants from '../Constants';
import { errorToast, toaster } from "../utils/toast";
import { getDateQueryParams, downloadFile } from "../utils/helper";
import { CSV_DATA_TYPE, CASE_TYPE, CASE_TYPE_DEMANDS, DEMAND, getDemandText, lifecarePlanDownloadBaseName } from "@demandproai/shared";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useProductContext } from '../contexts/ProductContext';
import { useExcludedDemands } from "./useExcludedDemands";

export const useDownloadOptions = (caseData) => {
    const { CASE_TYPE_DEMANDS_MT, DEFAULT_CASE_TYPE_MT, CASE_TYPE_DOWNLOAD_DEMANDS_MT } = useProductContext();
    const { demandNameMap } = useSelector((state) => state.demandMap);
    const { postData, getData } = useAxios();
    const { postData: postFiles } = usePfqAxios();

    const excludedDemands = useExcludedDemands(caseData);
    // A product whose download list differs from its template-management list
    // sets CASE_TYPE_DOWNLOAD_DEMANDS_MT; undefined everywhere else, so the
    // chain falls through to the existing behaviour.
    const resolvedDemands = CASE_TYPE_DOWNLOAD_DEMANDS_MT || CASE_TYPE_DEMANDS_MT || CASE_TYPE_DEMANDS;
    const defaultCaseType = DEFAULT_CASE_TYPE_MT ?? CASE_TYPE.AUTO_ACCIDENT;
    const caseType = caseData?.caseInfo?.caseType ?? defaultCaseType;

    const downloadOptions = useMemo(() => {
        if (!caseType) return [];
        return (resolvedDemands?.[caseType] || []).filter(
            (demandType) => !excludedDemands.includes(demandType),
        );
    }, [resolvedDemands, caseType, excludedDemands]);

    // Build the list of download option rows shown in the DownloadOptions menu
    const buildDownloadOptions = useCallback(({ onClose, onDownloadComplete }) => {
        if (!caseData) return { staticOptions: [], demandOptions: [] };

        const { _id, caseInfo, docxFiles } = caseData;
        const caseName = caseInfo?.caseName;
        const silentWitnessS3UrlPath = docxFiles?.silent_witness?.s3UrlPath;
        const auditReportS3UrlPath = docxFiles?.Case_Audit?.s3UrlPath;

        const runAndClose = (fn) => () => { fn(); onClose?.(); };

        const staticOptions = [
            {
                key: 'invoice',
                label: 'Invoice',
                onClick: runAndClose(() => downloadCaseInvoice({ caseId: _id, caseName })),
            },
            {
                key: 'medical-chronology',
                label: 'Medical Chronology',
                onClick: runAndClose(() => downloadDemandDraft({
                    caseId: _id,
                    typeOfDemandDraft: DEMAND.Medical_Chronology,
                    onDownloaded: onDownloadComplete,
                })),
            },
            {
                key: 'case-files',
                label: 'Case Files',
                onClick: runAndClose(() => downloadCaseFiles({ caseId: _id, caseName })),
            },
        ];

        if (silentWitnessS3UrlPath) {
            staticOptions.push({
                key: 'silent-witness',
                label: 'Silent Witness',
                onClick: runAndClose(() => downloadSingleFile({
                    s3UrlPath: silentWitnessS3UrlPath,
                    name: `${caseName}-silent-witness.docx`,
                })),
            });
        }

        // TODO fv added this false here because case audit needs to be updated for snapshot 
        //eslint-disable-next-line no-constant-binary-expression, no-constant-condition
        if (false && auditReportS3UrlPath) {
            staticOptions.push({
                key: 'case-audit',
                label: 'Case Audit',
                onClick: runAndClose(() => downloadSingleFile({
                    s3UrlPath: auditReportS3UrlPath,
                    name: `${caseName}-case-audit.pdf`,
                })),
            });
        }

        const demandOptions = downloadOptions.map((demandType) => {
            const label = getDemandText({
                state: caseInfo?.stateOfIncident,
                caseType,
                value: demandType,
                demandNameMap,
            });

            if (demandType === DEMAND.Lifecare_Plan) {
                return { key: demandType, kind: 'lifecare', demandType };
            }
            if (demandType === DEMAND.Certified_Lifecare_Plan) {
                return {
                    key: demandType,
                    kind: 'standard',
                    label,
                    onClick: runAndClose(() => downloadCertifiedLifecarePlan({
                        caseId: _id,
                        caseName,
                        onDownloaded: onDownloadComplete,
                    })),
                };
            }
            return {
                key: demandType,
                kind: 'standard',
                label,
                onClick: runAndClose(() => downloadDemandDraft({
                    caseId: _id,
                    typeOfDemandDraft: demandType,
                    onDownloaded: onDownloadComplete,
                })),
            };
        });

        return { staticOptions, demandOptions };
    }, [caseData, caseType, downloadOptions, demandNameMap]);

    const downloadDemandDraft = async ({ caseId, typeOfDemandDraft, clientId, onDownloaded }) => {
        if (!typeOfDemandDraft) {
            toaster({ message: 'Please select Type of Demand', success: false });
            return;
        }

        try {
            const { success, data, message } = await postFiles(Constants.ApiUrl.pfqDownload.generate, {
                caseId,
                typeOfDemandDraft,
                clientId
            }, {
                timeout: 60000 * 10
            });

            if (!success) {
                toaster({ message, success });
                return;
            }

            let { wordUrl } = data;
            if (!Array.isArray(wordUrl)) {
                wordUrl = [wordUrl];
            }

            for (const url of wordUrl) {
                await downloadFile({ fileUrl: url });
            }

            onDownloaded?.({ showLoader: false });
            toaster({ message: "File downloaded successfully", success: true });
        } catch (err) {
            toaster({ message: err.message || "Download failed", success: false });
        }
    };

    const downloadCaseInvoice = async ({ caseId, caseName }) => {
        try {
            const { success, data, message } = await postFiles(Constants.ApiUrl.pfqDownload.invoice, { caseId });

            if (success) {
                try {
                    const { wordUrl } = data;
                    await downloadFile({ fileName: `${caseName}-invoice.pdf`, fileUrl: wordUrl })
                    toaster({ message: "Invoice downloaded successfully", success: true });
                } catch (e) {
                    console.error("Error: ", e);
                    toaster({ message: "Error while processing invoice download", success: false });
                }
            } else {
                toaster({ message, success });
            }
        } catch (e) {
            console.error("Error: ", e);
            toaster({ message: "Invoice download failed", success: false });
        }
    };

    const downloadCaseFiles = async ({ caseId, caseName }) => {
        try {
            const { success, data, message } = await postFiles(Constants.ApiUrl.downloadCaseFilesZip, { caseId }, {
                timeout: 60000 * 10
            });
            const { zipUrl } = data;
            if (!success || !zipUrl) {
                toaster({ message: message, success: false });
                return
            }
            if (success) {
                try {
                    await downloadFile({ fileName: `${caseName}-files.zip`, fileUrl: zipUrl })
                    toaster({ message: "Files zip download started", success: true });
                } catch (e) {
                    console.log("Error: ", e)
                }
            } else {
                toaster({ message, success });
            }
        } catch (e) {
            console.log("Error: ", e)
            toaster({ message: "Failed to download files zip", success: false });
        }
    }

    const downloadSingleFile = async (file) => {
        try {
            const { success, data } = await postFiles(Constants.ApiUrl.pfqDownload.singleFile, { file });
            if (success) {
                await downloadFile({ fileUrl: data.s3UrlPath })
                toaster({ message: "File download started", success: success });
            } else {
                toaster({ message: "Not applicable to download", success: success })
            }
        } catch (e) {
            console.log("Error: ", e)
            toaster({ message: "Failed to download file", success: false });
        }
    }

    const downloadCsvFile = async (playload) => {
        try {
            const url = Constants.ApiUrl.super.dashboard.downloadCsv;
            const response = await postData(url, playload);
            if (!response.success) {
                toaster({ message: response.message || 'Error to Download CSV File', success: false });
                return;
            }
            await downloadFile({ fileUrl: response.downloadCsvLink })
            toaster({ message: 'CSV export successfully completed.', success: true });
        } catch (error) {
            errorToast('Error to Download CSV File:')
            console.error("Error to Download CSV File:", error);
        }
    }

    // for dashboard download butoon file  
    const downloadDashboardCsvFile = async ({ selectedRange, selectedTiers, selectedStatus }) => {
        const dateObj = getDateQueryParams(selectedRange);
        const filter = {
            ...dateObj,
            tier: selectedTiers,
            status: selectedStatus
        }
        const type = CSV_DATA_TYPE.COMPANIES_FULL_DATA;
        downloadCsvFile({ type, filter })
    }

    const getDocxVersionHistory = async ({ s3UrlPath }) => {
        try {
            const { success, data, message } = await postFiles(Constants.ApiUrl.version.history, JSON.stringify({ s3UrlPath }));
            if (!success) {
                toaster({ message: message || "Failed to load version history", success: false });
                return { success: false, versions: [] };
            }
            return { success: true, data };
        } catch (err) {
            toaster({ message: err.message || "Failed to load version history", success: false });
            return { success: false, versions: [] };
        }
    };

    const downloadDocxVersion = async ({ s3UrlPath, versionNumber, fileName, onDownloaded }) => {
        if (!s3UrlPath) {
            toaster({ message: 'Docx not available for this case.', success: false });
            return;
        }
        try {
            const { success, data, message } = await postFiles(Constants.ApiUrl.version.download, JSON.stringify({
                s3UrlPath,
                versionNumber
            }));
            if (!success) {
                toaster({ message: message || "Failed to download version", success: false });
                return;
            }
            await downloadFile({ fileName, fileUrl: data?.signedUrl });
            onDownloaded?.({ showLoader: false });
            toaster({ message: "File downloaded successfully", success: true });
        } catch (err) {
            toaster({ message: err.message || "Download failed", success: false });
        }
    };

    const downloadCaseFilesVersion = async ({ caseId, caseName, versionNumber, onDownloaded }) => {
        try {
            const { success, data, message } = await postFiles(Constants.ApiUrl.version.caseFilesDownload, JSON.stringify({
                caseId,
                versionNumber
            }), {
                timeout: 60000 * 10
            });
            if (!success) {
                toaster({ message: message || "Failed to download case files", success: false });
                return;
            }
            await downloadFile({ fileName: `${caseName}-files.zip`, fileUrl: data?.signedUrl });
            onDownloaded?.({ showLoader: false });
            toaster({ message: "Files zip download started", success: true });
        } catch (err) {
            toaster({ message: err.message || "Failed to download files zip", success: false });
        }
    };

    const downloadCertifiedLifecarePlan = async ({ caseId, caseName, onDownloaded }) => {
        try {
            const url = `${Constants.ApiUrl.certifiedLifecarePlan.download}/${caseId}`;
            const { success, data, message } = await getData(url);
            if (!success) {
                toaster({ message: message || "Failed to download Life Care Plan", success: false });
                return;
            }
            await downloadFile({ fileName: `${caseName}_lifecare-plan.zip`, fileUrl: data?.signedUrl });
            onDownloaded?.({ showLoader: false });
            toaster({ message: "Life Care Plan download started", success: true });
        } catch (err) {
            toaster({ message: err.message || "Life Care Plan download failed", success: false });
        }
    };

    const downloadCertifiedLifecarePlanVersion = async ({ s3UrlPath, caseName, versionNumber, onDownloaded }) => {
        if (!s3UrlPath) {
            toaster({ message: 'Preliminary Life Care Plan not available for this case.', success: false });
            return;
        }
        try {
            const { success, data, message } = await postFiles(
                Constants.ApiUrl.version.certifiedLifecarePlanDownload,
                JSON.stringify({ s3UrlPath, versionNumber, caseName })
            );
            if (!success) {
                toaster({ message: message || "Failed to download version", success: false });
                return;
            }
            await downloadFile({ fileName: `${caseName}_lifecare-plan.zip`, fileUrl: data?.signedUrl });
            onDownloaded?.({ showLoader: false });
            toaster({ message: "File downloaded successfully", success: true });
        } catch (err) {
            toaster({ message: err.message || "Download failed", success: false });
        }
    };

    const downloadLifecarePlanVersion = async ({ s3UrlPath, versionNumber, caseName, clientName, onDownloaded }) => {
        if (!s3UrlPath) {
            toaster({ message: 'Life Care Plan not available for this client.', success: false });
            return;
        }
        try {
            const { success, data, message } = await postFiles(
                Constants.ApiUrl.version.lifecarePlanDownload,
                JSON.stringify({ s3UrlPath, versionNumber, caseName, clientName })
            );
            if (!success) {
                toaster({ message: message || "Failed to download version", success: false });
                return;
            }
            const fileName = `${lifecarePlanDownloadBaseName({ caseName, clientName })}.pdf`;
            downloadFile({ fileName, fileUrl: data?.signedUrl });
            onDownloaded?.({ showLoader: false });
            toaster({ message: "File downloaded successfully", success: true });
        } catch (err) {
            toaster({ message: err.message || "Download failed", success: false });
        }
    };

    const downloadCostProjection = ({ caseId, clientId }) =>
        downloadDemandDraft({
            caseId,
            clientId,
            typeOfDemandDraft: DEMAND.Lifecare_Plan,
            onDownloaded: () => postData(Constants.ApiUrl.costProjection.record, { caseId }).catch(() => {})
        });

    return {
        downloadDemandDraft,
        downloadCostProjection,
        downloadCaseInvoice,
        downloadCaseFiles,
        downloadSingleFile,
        downloadDashboardCsvFile,
        getDocxVersionHistory,
        downloadDocxVersion,
        downloadCaseFilesVersion,
        downloadLifecarePlanVersion,
        downloadCertifiedLifecarePlan,
        downloadCertifiedLifecarePlanVersion,
        downloadCsvFile,
        downloadOptions,
        buildDownloadOptions
    };
};


